"use client";

import type { ChangeEvent } from "react";
import type { TextStandardInput } from "./form-types";
import type { StandardInputErrors } from "./standard-input-config";

import { cn } from "@homeinstead/ui";
import { Input } from "@homeinstead/ui/components/Input";
import { useId } from "react";
import { LeadFormErrorInline } from "./LeadFormErrorInline";
import { useLeadFormPersistedValues } from "./LeadFormPersistedValuesContext";
import {
  resolveLabel,
  resolvePlaceholder,
  TEXT_INPUT_CONFIG,
} from "./standard-input-config";

interface TextInputFieldProps {
  input: TextStandardInput;
  errors: StandardInputErrors;
  onFieldChange?: (name: string, value: string) => void;
}

export function TextInputField({
  input,
  errors,
  onFieldChange,
}: TextInputFieldProps) {
  const { inputType } = input;
  const config = TEXT_INPUT_CONFIG[inputType];
  const placeholder = resolvePlaceholder(input);
  const fieldErrors = errors?.[inputType] ?? [];
  const hasError = fieldErrors.length > 0;
  const uid = useId();
  const errorId = `${uid}-error`;
  const { persistedFieldValues } = useLeadFormPersistedValues();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onFieldChange?.(inputType, e.target.value);
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="sr-only" htmlFor={uid}>
        {resolveLabel(input)}
      </label>
      <Input
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        autoComplete={config.autoComplete}
        className={cn({ "border-red-500": hasError })}
        defaultValue={persistedFieldValues[inputType]}
        id={uid}
        inputMode={config.inputMode}
        name={inputType}
        onChange={handleChange}
        placeholder={placeholder}
        type={config.type}
      />
      {hasError ? (
        <LeadFormErrorInline id={errorId}>{fieldErrors[0]}</LeadFormErrorInline>
      ) : null}
    </div>
  );
}
